import { Button } from '@auctisync/ui';
import { useState, type FormEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { destinationForClaims, readClaims } from '../hooks/useSession';
import { supabase } from '../lib/supabase';

type SignInFormProps = {
  /**
   * Absolute URL Supabase should redirect the magic link to. Must be
   * allowlisted in Supabase Auth → URL Configuration → Redirect URLs.
   */
  magicLinkRedirectTo: string;
  /**
   * Where to send the user after a successful PASSWORD sign-in, overriding the
   * role-based default (`destinationForClaims`). Bidders have no role claim, so
   * the default would bounce them to `/unauthorized`; the bidder sign-in page
   * passes `/` here to route them into the house-routing home instead. A
   * ProtectedRoute `from` redirect still takes precedence over this.
   */
  defaultRedirect?: string;
  /**
   * Show the secondary "Email me a sign-in link" (magic-link) button. Staff
   * sign-in pages leave this on; the bidder page turns it off for a plain
   * email+password sign-in (bidders self-serve via the Sign Up page instead).
   */
  showMagicLink?: boolean;
};

type Mode = 'idle' | 'submitting' | 'magic-link-sent';

/**
 * Email + password sign-in with "Email me a sign-in link" secondary.
 *
 * Shared between `/admin` (tenant staff) and `/super` (super admin) sign-in
 * pages. After successful auth, we route by the user's role claims — a
 * super admin signing in at `/admin` lands on `/super/dashboard`, a tenant
 * user signing in at `/super` lands on `/admin/dashboard`. If the user
 * arrived here from a ProtectedRoute redirect, we honor that `from` path
 * instead (so deep links work).
 *
 * Note: native inputs are styled inline via theme CSS variables. When a
 * second form ships we'll extract Input + Label into `@auctisync/ui` as
 * shadcn-pattern components.
 */
export function SignInForm({
  magicLinkRedirectTo,
  defaultRedirect,
  showMagicLink = true,
}: SignInFormProps) {
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mode, setMode] = useState<Mode>('idle');
  const [error, setError] = useState<string | null>(null);

  // If ProtectedRoute redirected us here, honor the intended destination.
  const redirectedFrom = (location.state as { from?: Location } | null)?.from?.pathname ?? null;

  async function handlePasswordSignIn(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setMode('submitting');

    const { data, error: signInError } = await supabase.auth.signInWithPassword({
      email: email.trim(),
      password,
    });

    if (signInError) {
      setError(signInError.message);
      setMode('idle');
      return;
    }

    const destination =
      redirectedFrom ?? defaultRedirect ?? destinationForClaims(readClaims(data.session));
    await navigate(destination, { replace: true });
  }

  async function handleMagicLink() {
    if (!email.trim()) {
      setError('Enter your email first, then tap "Email me a sign-in link".');
      return;
    }
    setError(null);
    setMode('submitting');

    const { error: otpError } = await supabase.auth.signInWithOtp({
      email: email.trim(),
      options: { emailRedirectTo: magicLinkRedirectTo },
    });

    if (otpError) {
      setError(otpError.message);
      setMode('idle');
      return;
    }

    setMode('magic-link-sent');
  }

  if (mode === 'magic-link-sent') {
    return (
      <div
        role="status"
        style={{
          padding: '1rem',
          borderRadius: '0.5rem',
          background: 'var(--surface-muted, #f5f5f5)',
          color: 'var(--text, #111)',
          fontSize: '0.875rem',
          lineHeight: 1.5,
        }}
      >
        <strong>Check your email.</strong> We sent a sign-in link to <code>{email.trim()}</code>.
        The link expires in 1 hour.
      </div>
    );
  }

  const busy = mode === 'submitting';
  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '0.5rem 0.75rem',
    borderRadius: 'var(--radius-sm, 0.375rem)',
    border: '1px solid var(--border, #e5e7eb)',
    background: 'var(--surface, #ffffff)',
    color: 'var(--text, #111)',
    fontFamily: 'var(--font-body, system-ui)',
    fontSize: '0.875rem',
    outline: 'none',
  };
  const labelStyle: React.CSSProperties = {
    display: 'block',
    marginBottom: '0.25rem',
    fontSize: '0.75rem',
    fontWeight: 600,
    color: 'var(--text-muted, #6b7280)',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  };

  return (
    <form
      onSubmit={(e) => {
        void handlePasswordSignIn(e);
      }}
      style={{ display: 'grid', gap: '0.75rem' }}
    >
      <div>
        <label htmlFor="signin-email" style={labelStyle}>
          Email
        </label>
        <input
          id="signin-email"
          name="email"
          type="email"
          autoComplete="email"
          required
          disabled={busy}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          style={inputStyle}
        />
      </div>

      <div>
        <label htmlFor="signin-password" style={labelStyle}>
          Password
        </label>
        <input
          id="signin-password"
          name="password"
          type="password"
          autoComplete="current-password"
          required
          disabled={busy}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          style={inputStyle}
        />
      </div>

      {error && (
        <div
          role="alert"
          style={{
            fontSize: '0.875rem',
            color: 'var(--semantic-error, #b91c1c)',
            padding: '0.5rem 0.75rem',
            borderRadius: 'var(--radius-sm, 0.375rem)',
            background: 'var(--semantic-error-bg, #fef2f2)',
            border: '1px solid var(--semantic-error, #b91c1c)',
          }}
        >
          {error}
        </div>
      )}

      <Button type="submit" disabled={busy}>
        {busy ? 'Signing in…' : 'Sign in'}
      </Button>
      {showMagicLink && (
        <Button
          type="button"
          variant="outline"
          disabled={busy}
          onClick={() => {
            void handleMagicLink();
          }}
        >
          Email me a sign-in link
        </Button>
      )}
    </form>
  );
}
